import dcmjs from 'dcmjs';
import { DcmJsDatasetWrapper } from '~/domain/dicom/dcmJsWrapper';
import { MULTI_VALUE_SEPARATOR } from '~/domain/dicom/multiValuedString';

const PIXEL_DATA_TAG = '7FE00010';
const BINARY_VRS = new Set(['OB', 'OW', 'OF', 'OD', 'UN']);
const UNREADABLE_VALUE_PLACEHOLDER = '[Unable to display]';

export interface DicomTag {
    id: string;
    tag: string;
    name: string;
    vr: string;
    value: string;
    depth: number;
    children?: DicomTag[];
}

/**
 * Value representations whose every attribute is treated as patient
 * information: a date or time dates the patient's care, and a person name
 * names someone involved in it.
 */
const PATIENT_INFORMATION_VRS: ReadonlySet<string> = new Set([
    'DA',
    'DT',
    'TM',
    'PN',
]);

/**
 * Attributes outside the Patient group (0010), and of no VR above, treated
 * as patient information.
 */
const PATIENT_INFORMATION_TAGS: ReadonlySet<string> = new Set([
    '(0008,0050)', // AccessionNumber
    '(0008,0080)', // InstitutionName
    '(0008,0081)', // InstitutionAddress
    '(0008,0092)', // ReferringPhysicianAddress
    '(0008,0094)', // ReferringPhysicianTelephoneNumbers
    '(0008,1030)', // StudyDescription
    '(0008,103E)', // SeriesDescription
    '(0008,1040)', // InstitutionalDepartmentName
    '(0020,0010)', // StudyID
    '(0032,1060)', // RequestedProcedureDescription
    '(0038,0010)', // AdmissionID
    '(0038,0300)', // CurrentPatientLocation
    '(0040,0253)', // PerformedProcedureStepID
    '(0040,0254)', // PerformedProcedureStepDescription
    '(0040,1001)', // RequestedProcedureID
    '(0070,0080)', // ContentLabel
    '(0070,0081)', // ContentDescription
    '(3004,0006)', // DoseComment
    '(3006,0002)', // StructureSetLabel
    '(3006,0004)', // StructureSetName
    '(3006,0006)', // StructureSetDescription
    '(300A,0002)', // RTPlanLabel
    '(300A,0003)', // RTPlanName
    '(300A,0004)', // RTPlanDescription
]);

/** Whether the attribute `tag` holds patient information. */
export function isPatientInformationTag(
    tag: Pick<DicomTag, 'tag' | 'vr'>
): boolean {
    return (
        tag.tag.startsWith('(0010,') ||
        PATIENT_INFORMATION_VRS.has(tag.vr) ||
        PATIENT_INFORMATION_TAGS.has(tag.tag)
    );
}

/**
 * Whether `tag.value` is the attribute's own text, rather than a sequence's
 * item count or a placeholder standing in for data that has no text form.
 */
export function hasLiteralValue(tag: DicomTag): boolean {
    return (
        !tag.children &&
        tag.value !== '' &&
        !BINARY_VRS.has(tag.vr) &&
        tag.value !== UNREADABLE_VALUE_PLACEHOLDER
    );
}

function formatValue(vr: string, element: { Value?: unknown[] }): string {
    if (!element.Value || element.Value.length === 0) {
        return '';
    }

    if (BINARY_VRS.has(vr)) {
        return '[Binary data]';
    }

    try {
        const parts = element.Value.map((v: unknown) =>
            typeof v === 'object' ? JSON.stringify(v) : String(v ?? '')
        );

        if ((vr === 'DS' || vr === 'IS') && parts.length > 1) {
            return `[${parts.join(', ')}]`;
        }

        return parts.join(MULTI_VALUE_SEPARATOR);
    } catch {
        return UNREADABLE_VALUE_PLACEHOLDER;
    }
}

function extractTagsRecursive(
    denaturalized: Record<string, { vr: string; Value?: unknown[] }>,
    depth: number,
    idPrefix: string
): DicomTag[] {
    const tags: DicomTag[] = [];

    for (const rawTag in denaturalized) {
        if (rawTag === PIXEL_DATA_TAG) {
            continue;
        }

        // punctuateTag returns undefined for a key that is neither
        // comma-punctuated nor 8 hex digits. Fall back to the raw key so a
        // malformed tag is still listed under a name the user can act on,
        // rather than sorting into a TypeError that empties the viewer.
        const punctuated =
            dcmjs.data.DicomMetaDictionary.punctuateTag(rawTag) ?? rawTag;
        const entry = dcmjs.data.DicomMetaDictionary.dictionary[punctuated];
        const element = denaturalized[rawTag] as {
            vr: string;
            Value?: unknown[];
        };
        const vr = element.vr ?? entry?.vr ?? '';
        const id = `${idPrefix}${rawTag}`;

        if (vr === 'SQ' && element.Value && element.Value.length > 0) {
            const itemCount = element.Value.length;
            const children: DicomTag[] = [];

            const multiItem = element.Value.length > 1;

            element.Value.forEach((item, index) => {
                if (multiItem) {
                    children.push({
                        id: `${id}._item${index}`,
                        tag: '',
                        name: `Item ${index + 1}`,
                        vr: '',
                        value: '',
                        depth: depth + 1,
                    });
                }

                const childDepth = multiItem ? depth + 2 : depth + 1;
                const itemTags = extractTagsRecursive(
                    item as Record<string, { vr: string; Value?: unknown[] }>,
                    childDepth,
                    `${id}.${index}.`
                );
                // Safe to spread despite the hazard noted on `appendTags`:
                // this is the elements of one sequence *item*, which is a
                // handful, not the flattened descendants of the sequence.
                children.push(...itemTags);
            });

            tags.push({
                id,
                tag: punctuated,
                name: entry?.name ?? 'Unknown',
                vr,
                value: `${itemCount} ${itemCount === 1 ? 'item' : 'items'}`,
                depth,
                children,
            });
        } else {
            tags.push({
                id,
                tag: punctuated,
                name: entry?.name ?? 'Unknown',
                vr,
                value: formatValue(vr, element),
                depth,
            });
        }
    }

    tags.sort((a, b) => a.tag.localeCompare(b.tag));
    return tags;
}

/**
 * Extract all DICOM tags from a dcmjs dataset wrapper as a tree.
 * Sequences are recursively expanded with child items.
 * Skips PixelData to avoid huge binary blobs.
 */
export function extractDicomTags(wrapper: DcmJsDatasetWrapper): DicomTag[] {
    if (!wrapper.dataset) {
        return [];
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const denaturalized = dcmjs.data.DicomMetaDictionary.denaturalizeDataset(
        wrapper.dataset as Record<string, unknown>
    );

    return extractTagsRecursive(denaturalized, 0, '');
}

/**
 * Append `tags` and the children we descend into to `result`, in display
 * order. A null `expanded` descends into every sequence; otherwise only into
 * the sequences it names.
 *
 * The accumulator is threaded through instead of concatenating each recursive
 * result, because `result.push(...children)` passes every child as a separate
 * argument. A structure set's top-level sequence holds tens of thousands of
 * descendants, which is enough to overflow the argument stack.
 */
function appendTags(
    tags: DicomTag[],
    expanded: ReadonlySet<string> | null,
    result: DicomTag[]
): void {
    for (const tag of tags) {
        result.push(tag);
        if (tag.children && (expanded === null || expanded.has(tag.id))) {
            appendTags(tag.children, expanded, result);
        }
    }
}

/** Flatten a tag tree into a single list (for search mode). */
export function flattenAllTags(tags: DicomTag[]): DicomTag[] {
    const result: DicomTag[] = [];
    appendTags(tags, null, result);
    return result;
}

/** Flatten only expanded nodes for tree display. */
export function flattenVisibleTags(
    tags: DicomTag[],
    expanded: ReadonlySet<string>
): DicomTag[] {
    const result: DicomTag[] = [];
    appendTags(tags, expanded, result);
    return result;
}
